import { TIMEFRAMES, type Bar, type Timeframe } from './chartModel';
import type { ChartColors } from './chartColors';

/** MT5's chart properties (F8) that are not toolbar toggles already: what is drawn, the scale and the colours. */
export interface ChartProperties {
  grid: boolean;
  /** Tick volumes as a histogram along the bottom of the price window. */
  volumes: boolean;
  /** Dotted vertical lines where a day (week, month, year) starts. */
  periodSeparators: boolean;
  /** Space to the right of the newest bar. */
  chartShift: boolean;
  scale: 'auto' | 'log' | 'fixed';
  /** The price range when the scale is fixed. */
  fixedMin: number | null;
  fixedMax: number | null;
  /** The account's positions, orders and their stops on the chart. */
  tradeLevels: boolean;
  /** Colours of this chart only; left out, the theme's. */
  colors: ChartColorOverrides;
}

export interface ChartColorOverrides {
  background?: string;
  foreground?: string;
  grid?: string;
  bull?: string;
  bear?: string;
}

export const COLOR_KEYS = ['background', 'foreground', 'grid', 'bull', 'bear'] as const;
export const COLOR_LABELS: Record<(typeof COLOR_KEYS)[number], string> = {
  background: 'Background',
  foreground: 'Text and scales',
  grid: 'Grid',
  bull: 'Bull candle',
  bear: 'Bear candle',
};

export const DEFAULT_PROPERTIES: ChartProperties = {
  grid: true,
  volumes: false,
  periodSeparators: false,
  chartShift: true,
  scale: 'auto',
  fixedMin: null,
  fixedMax: null,
  tradeLevels: true,
  colors: {},
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Reads stored properties, which may be missing, old or damaged. */
export function normalizeProperties(raw: unknown): ChartProperties {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<
    Record<keyof ChartProperties, unknown>
  >;
  const bool = (key: 'grid' | 'volumes' | 'periodSeparators' | 'chartShift' | 'tradeLevels') =>
    typeof r[key] === 'boolean' ? (r[key] as boolean) : DEFAULT_PROPERTIES[key];
  const colorsRaw = (typeof r.colors === 'object' && r.colors !== null ? r.colors : {}) as Record<
    string,
    unknown
  >;
  const colors: ChartColorOverrides = {};
  for (const key of COLOR_KEYS) {
    const value = colorsRaw[key];
    if (typeof value === 'string' && HEX.test(value)) colors[key] = value;
  }
  let fixedMin = finite(r.fixedMin);
  let fixedMax = finite(r.fixedMax);
  let scale: ChartProperties['scale'] = r.scale === 'log' || r.scale === 'fixed' ? r.scale : 'auto';
  if (scale === 'fixed' && (fixedMin === null || fixedMax === null || fixedMin >= fixedMax)) {
    scale = 'auto';
    fixedMin = fixedMax = null;
  }
  return {
    grid: bool('grid'),
    volumes: bool('volumes'),
    periodSeparators: bool('periodSeparators'),
    chartShift: bool('chartShift'),
    scale,
    fixedMin,
    fixedMax,
    tradeLevels: bool('tradeLevels'),
    colors,
  };
}

/** The theme's colours with this chart's own on top. */
export function chartColorsWith(
  theme: ChartColors,
  own: ChartColorOverrides,
): ChartColors & { background: string | null } {
  return {
    ...theme,
    up: own.bull ?? theme.up,
    down: own.bear ?? theme.down,
    muted: own.foreground ?? theme.muted,
    border: own.grid ?? theme.border,
    // the chart's own colours win over the theme's candle colours too
    ...(own.bull ? { barUp: own.bull, bullBody: own.bull } : {}),
    ...(own.bear ? { barDown: own.bear, bearBody: own.bear } : {}),
    background: own.background ?? theme.chartBg ?? null,
  };
}

const DAY = 86_400;

/**
 * Where MT5 draws period separators: at each new day on charts up to H1, each new week on H2 to H12 (weeks start on
 * Sunday), each new month on D1, and each new year on W1 and MN1. Bar times are server-clock seconds.
 */
export function periodStarts(bars: readonly Bar[], tf: Timeframe): number[] {
  const index = TIMEFRAMES.indexOf(tf);
  const key = (t: number) => {
    const d = new Date(t * 1000);
    if (index <= TIMEFRAMES.indexOf('H1')) return Math.floor(t / DAY);
    if (index <= TIMEFRAMES.indexOf('H12')) return Math.floor((Math.floor(t / DAY) + 4) / 7); // day 0 was a Thursday
    if (tf === 'D1') return d.getUTCFullYear() * 12 + d.getUTCMonth();
    return d.getUTCFullYear();
  };
  const out: number[] = [];
  for (let i = 1; i < bars.length; i++) {
    if (key(bars[i]!.t) !== key(bars[i - 1]!.t)) out.push(bars[i]!.t);
  }
  return out;
}

/**
 * MT5's quick navigation: typed on a chart, "GBPUSD", "h4", "gbpusd h4" or "GBPUSD,M15" changes the symbol, the timeframe
 * or both. A symbol is matched exactly (any case), or by a prefix that only one symbol has. Null when nothing matches.
 */
export function parseQuickNavigation(
  text: string,
  symbols: readonly string[],
): { symbol?: string; tf?: Timeframe } | null {
  const tokens = text
    .trim()
    .toUpperCase()
    .split(/[\s,;]+/)
    .filter(Boolean);
  if (tokens.length === 0 || tokens.length > 2) return null;
  const out: { symbol?: string; tf?: Timeframe } = {};
  for (const token of tokens) {
    if ((TIMEFRAMES as readonly string[]).includes(token) && !out.tf) {
      out.tf = token as Timeframe;
      continue;
    }
    const exact = symbols.find((s) => s.toUpperCase() === token);
    const prefixed = symbols.filter((s) => s.toUpperCase().startsWith(token));
    const match = exact ?? (prefixed.length === 1 ? prefixed[0] : undefined);
    if (!match || out.symbol) return null;
    out.symbol = match;
  }
  return out;
}
